/**
 * shopifyStorefront.js
 * --------------------
 * Read-only Shopify Storefront API client used by /shop Team Set
 * sections. Shopify remains the single source of truth for price,
 * inventory, images, product titles, availability, and URLs.
 *
 * Only PUBLISHED + ACTIVE products are accessible through the
 * Storefront API — Draft / unpublished products are correctly
 * hidden by Shopify. We do NOT try to work around this: the UI
 * simply renders however many products the Storefront returns
 * (0 during initial import, ramping up as products are activated).
 *
 * Endpoint: <shop>.myshopify.com/api/2024-10/graphql.json
 * Auth: X-Shopify-Storefront-Access-Token (public client credential,
 *       already used by ShopifyBuyButton.jsx and shopifyCart.js).
 */

const SHOPIFY_DOMAIN = "hvn653-ee.myshopify.com";
const SHOPIFY_STOREFRONT_TOKEN = "b327df1ea4faa63e2e12bed8c6f090ed";
const API_URL = `https://${SHOPIFY_DOMAIN}/api/2024-10/graphql.json`;

// Auto-generated Shopify collection handles for the 3 Team Set groups.
// These match the collection titles the merchant created in Shopify:
//   "Gold Crumple Full Team Sets", "Blue Parallel Full Team Sets",
//   "White Full Team Sets". Shopify slugifies the title into the handle.
const COLLECTION_HANDLE_BY_VARIATION = {
  gold:  "gold-crumple-full-team-sets",
  blue:  "blue-parallel-full-team-sets",
  white: "white-full-team-sets",
};

// Manual "featured" collections used on the main /shop page. Each of these
// contains exactly 12 hand-picked products in a merchant-defined order.
// Because these are MANUAL collections in Shopify, GraphQL will preserve
// that order when queried with `sortKey: MANUAL`. The View-All pages
// (/shop/team-sets/{variation}) continue to use the full-catalog handles
// above.
const FEATURED_COLLECTION_HANDLE_BY_VARIATION = {
  gold:  "featured-gold-team-sets",
  blue:  "featured-blue-team-sets",
  white: "featured-white-team-sets",
};

// Gold Crumple Singles automated collections (Shopify smart collections
// keyed by price). Products carry the tag `gold-single` and one of
// `sticker-type-regular` / `sticker-type-team-badge` / `sticker-type-fwc`.
export const GOLD_SINGLES_COLLECTION_HANDLES = {
  all:       "gold-singles-all",
  premium:   "gold-singles-premium-20",   // $20+   (28 products)
  midtier:   "gold-singles-10-19",        // $10-19 (72 products)
  underten:  "gold-singles-9-under",      // <=$9   (880 products)
};

const COLLECTION_PRODUCTS_QUERY = /* GraphQL */ `
  query TeamSetsByCollection($handle: String!, $first: Int!, $sortKey: ProductCollectionSortKeys) {
    collection(handle: $handle) {
      id
      title
      handle
      products(first: $first, sortKey: $sortKey) {
        edges {
          node {
            id
            handle
            title
            availableForSale
            onlineStoreUrl
            tags
            featuredImage { url altText }
            priceRange {
              minVariantPrice { amount currencyCode }
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  sku
                  availableForSale
                  price { amount currencyCode }
                }
              }
            }
          }
        }
      }
    }
  }
`;

function _formatMoney(amount, currency) {
  const n = Number(amount || 0);
  if (!Number.isFinite(n)) return null;
  const opts = { style: "currency", currency: currency || "USD" };
  try { return new Intl.NumberFormat("en-US", opts).format(n); }
  catch { return `$${n.toFixed(2)}`; }
}

function _availability(node) {
  // Storefront token used by the Buy Button channel does NOT have the
  // `unauthenticated_read_product_inventory` scope, so we cannot read
  // exact `quantityAvailable`. We rely on the variant's boolean
  // `availableForSale` — the source of truth for whether Shopify can
  // sell it right now (inventory > 0, or inventory policy = continue).
  const v = (node.variants && node.variants.edges[0] && node.variants.edges[0].node) || {};
  if (v.availableForSale === false || node.availableForSale === false) return "SOLD_OUT";
  return "IN_STOCK";
}

/**
 * Parse country name + code from Shopify product metadata.
 *
 * Priority:
 *   1. Country code from a `country-xxx` tag (canonical, always present
 *      when the CSV import ran).
 *   2. Country code from the SKU: `WC26-XXX-{WHITE|BLUE|GOLD}-SET`.
 *   3. Country name from the product title portion before " — ".
 */
function _extractCountry(node) {
  const tags = Array.isArray(node.tags) ? node.tags : [];
  let cc = null;
  for (const t of tags) {
    const m = /^country-([a-z]{3})$/i.exec(String(t).trim());
    if (m) { cc = m[1].toUpperCase(); break; }
  }
  if (!cc) {
    const sku = (node.variants && node.variants.edges[0] && node.variants.edges[0].node && node.variants.edges[0].node.sku) || "";
    const m = /^WC26-([A-Z]{3})-(WHITE|BLUE|GOLD)-SET$/i.exec(sku);
    if (m) cc = m[1].toUpperCase();
  }
  const title = String(node.title || "");
  const country = title.split(" — ")[0].trim() || null;
  return { country, country_code: cc };
}

function _variationFromTags(tags) {
  const t = (tags || []).map((x) => String(x).toLowerCase());
  if (t.includes("team-set-gold"))  return "gold";
  if (t.includes("team-set-blue"))  return "blue";
  if (t.includes("team-set-white")) return "white";
  return null;
}

/**
 * Map a Storefront product node to the shape the existing
 * `TeamSetCard` component expects. Keeps the component contract
 * unchanged so nothing downstream needs a redesign.
 */
export function mapProductNode(node) {
  const v = (node.variants && node.variants.edges[0] && node.variants.edges[0].node) || {};
  const { country, country_code } = _extractCountry(node);
  const price = v.price
    ? _formatMoney(v.price.amount, v.price.currencyCode)
    : (node.priceRange && node.priceRange.minVariantPrice
        ? _formatMoney(node.priceRange.minVariantPrice.amount, node.priceRange.minVariantPrice.currencyCode)
        : null);
  const priceAmount = _priceAmount(node);
  return {
    country,
    country_code,
    variation:            _variationFromTags(node.tags),
    product_title:        node.title,
    image_url:            (node.featuredImage && node.featuredImage.url) || null,
    price,
    _price_amount:        priceAmount,           // numeric — used for client-side sort on the Team Sets catalog page
    availability:         _availability(node),
    shopify_product_id:   node.id,
    shopify_variant_id:   v.id || null,
    shopify_handle:       node.handle,
    // NOTE: Shopify's `onlineStoreUrl` returns the merchant's configured
    // PRIMARY customer-facing domain (in this store: www.gilmaakaroun.com),
    // which is NOT the CollectorUp storefront. CollectorUp must never send
    // Team Set customers to gilmaakaroun.com. We ignore `onlineStoreUrl`
    // and always build against the canonical hvn653-ee.myshopify.com
    // storefront (same domain the shared Shopify Buy SDK cart uses).
    product_url:          node.handle ? `https://${SHOPIFY_DOMAIN}/products/${node.handle}` : null,
    featured:             false,
  };
}

/**
 * Fetch products from a Team Set collection by handle.
 *
 * @param {string} handle    - Shopify collection handle.
 * @param {"gold"|"blue"|"white"} variation - used to filter by tag.
 * @param {number} limit     - max products returned.
 * @param {"MANUAL"|"BEST_SELLING"|"CREATED"} [sortKey="MANUAL"] - preserve
 *        the merchant's manual ordering by default; MANUAL is the correct
 *        sort key for manual collections and hand-curated featured sets.
 */
async function _fetchByHandle(handle, variation, limit, sortKey = "MANUAL") {
  if (!handle) return { products: [], meta: { handle: null, count: 0, source: "empty", error: "no-handle" } };
  try {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
        "Accept": "application/json",
      },
      body: JSON.stringify({
        query: COLLECTION_PRODUCTS_QUERY,
        variables: { handle, first: Math.min(Math.max(limit, 1), 250), sortKey },
      }),
    });
    if (!res.ok) {
      return { products: [], meta: { handle, count: 0, source: "error", error: `http-${res.status}` } };
    }
    const body = await res.json();
    if (body.errors && body.errors.length) {
      return { products: [], meta: { handle, count: 0, source: "error", error: body.errors[0].message } };
    }
    const collection = body.data && body.data.collection;
    if (!collection) {
      return { products: [], meta: { handle, count: 0, source: "empty", error: "collection-not-found" } };
    }
    const edges = (collection.products && collection.products.edges) || [];
    const products = edges.map((e) => mapProductNode(e.node)).filter((p) => p.variation === variation);
    return { products, meta: { handle, count: products.length, source: products.length ? "shopify" : "empty" } };
  } catch (e) {
    return { products: [], meta: { handle, count: 0, source: "error", error: String(e && e.message || e) } };
  }
}

/**
 * Fetch products from the FULL catalog collection for a given variation
 * (used by the View All page at /shop/team-sets/{variation}).
 *
 * @param {"gold"|"blue"|"white"} variation
 * @param {number} [limit=12]
 */
export async function fetchTeamSets(variation, limit = 12) {
  return _fetchByHandle(COLLECTION_HANDLE_BY_VARIATION[variation], variation, limit, "MANUAL");
}

/**
 * Fetch products from the hand-curated FEATURED collection for a given
 * variation (used by the main /shop Team Set section). Order is preserved
 * from the merchant's manual sort in Shopify.
 *
 * @param {"gold"|"blue"|"white"} variation
 * @param {number} [limit=12]
 */
export async function fetchFeaturedTeamSets(variation, limit = 12) {
  return _fetchByHandle(FEATURED_COLLECTION_HANDLE_BY_VARIATION[variation], variation, limit, "MANUAL");
}

// Query used to fetch the Featured Collectibles grid (arbitrary
// collection by Shopify GID, no variation filter — accepts ALL product
// types since the "All Products" collection includes packs, boxes,
// albums, Blue Parallel Editions, etc.).
const COLLECTION_BY_ID_QUERY = /* GraphQL */ `
  query CollectionById($id: ID!, $first: Int!, $sortKey: ProductCollectionSortKeys) {
    collection(id: $id) {
      id title handle
      products(first: $first, sortKey: $sortKey) {
        edges { node {
          id handle title availableForSale onlineStoreUrl tags
          featuredImage { url altText }
          priceRange { minVariantPrice { amount currencyCode } }
          variants(first: 1) {
            edges { node { id sku availableForSale price { amount currencyCode } } }
          }
        }}
      }
    }
  }
`;

// ============================================================
// GOLD CRUMPLE SINGLES
// ============================================================
//
// Two query shapes:
//   1. HEAVY (`GOLD_PREMIUM_QUERY`) — used ONLY for the 28 Premium $20+
//      products. Includes the featured image so we can render image-driven
//      visual cards on /shop and inside /shop/gold-singles.
//   2. LIGHT (`GOLD_LIGHT_QUERY`)   — used for the $10–$19 (72) and
//      $9 & Under (880) collections. Skips the image field entirely so
//      the payload stays small and the compact list rows load fast.
//
// Search across the full 980-product catalog is enabled by fetching ALL
// mid + value products upfront (with cursor pagination in batches of
// 250) and keeping them in a client-side in-memory index. This lets the
// user search for anything at any time — the pagination shown in the UI
// for the $9 & Under bucket is purely a rendering concern.

const GOLD_PREMIUM_QUERY = /* GraphQL */ `
  query GoldPremium($handle: String!, $first: Int!, $sortKey: ProductCollectionSortKeys, $reverse: Boolean) {
    collection(handle: $handle) {
      id title handle
      products(first: $first, sortKey: $sortKey, reverse: $reverse) {
        edges { node {
          id handle title availableForSale tags
          featuredImage { url altText }
          priceRange { minVariantPrice { amount currencyCode } }
          variants(first: 1) {
            edges { node { id sku availableForSale price { amount currencyCode } } }
          }
        }}
      }
    }
  }
`;

const GOLD_LIGHT_QUERY = /* GraphQL */ `
  query GoldLight($handle: String!, $first: Int!, $after: String, $sortKey: ProductCollectionSortKeys, $reverse: Boolean) {
    collection(handle: $handle) {
      id title handle
      products(first: $first, after: $after, sortKey: $sortKey, reverse: $reverse) {
        pageInfo { hasNextPage endCursor }
        edges { node {
          id handle title availableForSale tags
          priceRange { minVariantPrice { amount currencyCode } }
          variants(first: 1) {
            edges { node { id sku availableForSale price { amount currencyCode } } }
          }
        }}
      }
    }
  }
`;

// Extract a Panini-style sticker code like ARG17 / FWC12 / BRA6 from the
// product title. Format: `<CODE> <Player/Subject> — Gold Crumple Single`.
function _extractStickerCode(title) {
  const t = String(title || "").trim();
  const m = /^([A-Z]{2,4}\d{1,3})\b/.exec(t);
  return m ? m[1] : null;
}

// Everything after the sticker code and before " — " (or the whole thing
// if there's no em-dash). Falls back to the raw title.
function _extractDisplayName(title, stickerCode) {
  let t = String(title || "").trim();
  if (stickerCode && t.startsWith(stickerCode)) t = t.slice(stickerCode.length).trim();
  const emDash = t.indexOf(" — ");
  const enDash = t.indexOf(" – ");
  const dash   = t.indexOf(" - ");
  const cut = [emDash, enDash, dash].filter(i => i > 0).sort((a,b)=>a-b)[0];
  if (cut && cut > 0) t = t.slice(0, cut).trim();
  return t;
}

function _stickerTypeFromTags(tags) {
  const t = (tags || []).map(x => String(x).toLowerCase());
  if (t.includes("sticker-type-fwc"))         return "fwc";
  if (t.includes("sticker-type-team-badge"))  return "team-badge";
  if (t.includes("sticker-type-regular"))     return "regular";
  return "regular";
}

function _priceAmount(node) {
  const v = (node.variants && node.variants.edges[0] && node.variants.edges[0].node) || {};
  if (v.price && v.price.amount != null) return Number(v.price.amount);
  if (node.priceRange && node.priceRange.minVariantPrice)
    return Number(node.priceRange.minVariantPrice.amount);
  return NaN;
}

/**
 * Map a Gold Singles product node to the shape the GoldSingleCard /
 * GoldSingleRow components expect. Set `withImage=false` for the light
 * mid-tier / value-tier fetches — the mapping still returns
 * `image_url: null` in that mode so the row renderers stay pure.
 */
export function mapGoldSingleNode(node, { withImage = true } = {}) {
  const v = (node.variants && node.variants.edges[0] && node.variants.edges[0].node) || {};
  const stickerCode = _extractStickerCode(node.title);
  const displayName = _extractDisplayName(node.title, stickerCode);
  const stickerType = _stickerTypeFromTags(node.tags);
  const tags = Array.isArray(node.tags) ? node.tags : [];
  // Country code from tags like `country-arg`.
  let countryCode = null;
  for (const t of tags) {
    const m = /^country-([a-z]{3})$/i.exec(String(t).trim());
    if (m) { countryCode = m[1].toUpperCase(); break; }
  }
  const priceAmount = _priceAmount(node);
  const priceStr = Number.isFinite(priceAmount)
    ? _formatMoney(priceAmount, (v.price && v.price.currencyCode) || "USD")
    : null;
  return {
    sticker_code:       stickerCode,
    subject_name:       displayName,
    player_name:        stickerType === "regular" ? displayName : null,
    country:            null,                 // full country name is not on Storefront node; UI uses country_code
    country_code:       countryCode,
    sticker_type:       stickerType,
    product_title:      node.title,
    image_url:          withImage ? ((node.featuredImage && node.featuredImage.url) || null) : null,
    price:              priceStr,
    _price_amount:      priceAmount,          // numeric — used for client-side sort
    availability:       (v.availableForSale === false || node.availableForSale === false) ? "SOLD_OUT" : "IN_STOCK",
    shopify_product_id: node.id,
    shopify_variant_id: v.id || null,
    shopify_handle:     node.handle,
    // Never use `onlineStoreUrl` — Shopify substitutes the merchant's
    // primary domain (gilmaakaroun.com) which we must not send customers to.
    product_url:        node.handle ? `https://${SHOPIFY_DOMAIN}/products/${node.handle}` : null,
    tags,
  };
}

/**
 * Fetch the Premium Gold Singles collection ($20+) — the ONLY Gold bucket
 * that renders as image-driven visual cards. Sorted by PRICE DESC in the
 * Shopify query so Messi / Ronaldo / Yamal naturally land at the top.
 *
 * @returns {Promise<{products: object[], meta: object}>}
 */
export async function fetchGoldSinglesPremium(limit = 28) {
  const handle = GOLD_SINGLES_COLLECTION_HANDLES.premium;
  try {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
        "Accept": "application/json",
      },
      body: JSON.stringify({
        query: GOLD_PREMIUM_QUERY,
        variables: { handle, first: Math.min(limit, 50), sortKey: "PRICE", reverse: true },
      }),
    });
    if (!res.ok) return { products: [], meta: { source: "error", error: `http-${res.status}` } };
    const body = await res.json();
    if (body.errors && body.errors.length) {
      return { products: [], meta: { source: "error", error: body.errors[0].message } };
    }
    const collection = body.data && body.data.collection;
    if (!collection) return { products: [], meta: { source: "empty", error: "collection-not-found" } };
    const edges = (collection.products && collection.products.edges) || [];
    const products = edges.map((e) => mapGoldSingleNode(e.node, { withImage: true }));
    return { products, meta: { handle, count: products.length, source: "shopify" } };
  } catch (e) {
    return { products: [], meta: { source: "error", error: String(e && e.message || e) } };
  }
}

/**
 * Fetch the FULL contents of one Gold Singles collection using LIGHT
 * payloads (no images). Handles Storefront cursor pagination internally
 * so the caller gets the full array back in a single await.
 *
 * Used for the $10–$19 (72 total, 1 page) and $9 & Under (880 total,
 * ~4 pages) collections. The full arrays are cached in memory by the
 * page component so the search bar can hit every product regardless of
 * whether it has been rendered yet.
 *
 * @param {"midtier"|"underten"} bucket
 * @param {number} [pageSize=250]
 * @returns {Promise<{products: object[], meta: object}>}
 */
export async function fetchAllGoldSinglesLight(bucket, pageSize = 250) {
  const handle = GOLD_SINGLES_COLLECTION_HANDLES[bucket];
  if (!handle) return { products: [], meta: { source: "error", error: "unknown-bucket" } };
  const out = [];
  let cursor = null;
  let pages = 0;
  try {
    while (true) {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
          "Accept": "application/json",
        },
        body: JSON.stringify({
          query: GOLD_LIGHT_QUERY,
          variables: {
            handle,
            first: Math.min(pageSize, 250),
            after: cursor,
            sortKey: "PRICE",
            reverse: true,
          },
        }),
      });
      if (!res.ok) return { products: out, meta: { source: "error", error: `http-${res.status}`, pages } };
      const body = await res.json();
      if (body.errors && body.errors.length) {
        return { products: out, meta: { source: "error", error: body.errors[0].message, pages } };
      }
      const collection = body.data && body.data.collection;
      if (!collection) return { products: out, meta: { source: "empty", error: "collection-not-found", pages } };
      const edges = (collection.products && collection.products.edges) || [];
      for (const e of edges) out.push(mapGoldSingleNode(e.node, { withImage: false }));
      pages += 1;
      const info = collection.products.pageInfo || {};
      if (!info.hasNextPage) break;
      cursor = info.endCursor;
      if (pages > 20) break; // hard safety cap
    }
    return { products: out, meta: { handle, count: out.length, pages, source: "shopify" } };
  } catch (e) {
    return { products: out, meta: { source: "error", error: String(e && e.message || e), pages } };
  }
}

/**
 * Fetch products from a Shopify collection by its numeric ID (encoded
 * as a Shopify GID: `gid://shopify/Collection/{id}`). Used by the
 * Featured Collectibles grid — same source the old Buy Button embed
 * pointed at, so no product duplication.
 */
export async function fetchCollectionByGid(gid, limit = 24) {
  if (!gid) return { products: [], meta: { source: "empty", error: "no-gid" } };
  try {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
        "Accept": "application/json",
      },
      body: JSON.stringify({
        query: COLLECTION_BY_ID_QUERY,
        variables: { id: gid, first: Math.min(Math.max(limit, 1), 50), sortKey: "MANUAL" },
      }),
    });
    if (!res.ok) return { products: [], meta: { source: "error", error: `http-${res.status}` } };
    const body = await res.json();
    if (body.errors && body.errors.length) {
      return { products: [], meta: { source: "error", error: body.errors[0].message } };
    }
    const collection = body.data && body.data.collection;
    if (!collection) return { products: [], meta: { source: "empty", error: "collection-not-found" } };
    const edges = (collection.products && collection.products.edges) || [];
    const products = edges.map((e) => mapProductNode(e.node));
    return { products, meta: { gid, count: products.length, source: products.length ? "shopify" : "empty" } };
  } catch (e) {
    return { products: [], meta: { source: "error", error: String(e && e.message || e) } };
  }
}
